"use client";

import { useCallback, useRef, useState } from "react";
import { formatEther } from "viem";

interface UseBurnAnimationsOptions {
  address?: string;
  multiplierWindow?: number;
  maxMultiplier?: number;
}

export function useBurnAnimations(options: UseBurnAnimationsOptions = {}) {
  const { address, multiplierWindow = 60000, maxMultiplier = 10 } = options;
  const [beraEyesFire, setBeraEyesFire] = useState(false);
  const [burnIntensity, setBurnIntensity] = useState(0);
  const [screenShake, setScreenShake] = useState(false);
  const [userBurnCount, setUserBurnCount] = useState(0);
  const [burnStreak, setBurnStreak] = useState(0);
  const [lastBurnTime, setLastBurnTime] = useState<number>(0);
  const [showMultiplier, setShowMultiplier] = useState(false);
  const multiplierTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const resetAnimations = useCallback(() => {
    setBeraEyesFire(false);
    setBurnIntensity(0);
    setScreenShake(false);
    setUserBurnCount(0);
    setBurnStreak(0);
    setShowMultiplier(false);
    setLastBurnTime(0);
    if (multiplierTimeoutRef.current) {
      clearTimeout(multiplierTimeoutRef.current);
      multiplierTimeoutRef.current = null;
    }
  }, []);

  const triggerBurnAnimation = useCallback(
    (
      burn: { id: string; amount: string; from?: string; source?: string; timestamp?: string },
      _isRealTimeBurn = true,
    ) => {
      const burnAmount = Number(formatEther(BigInt(burn.amount)));
      const now = Date.now();
      const isWithinWindow = lastBurnTime > 0 && now - lastBurnTime < multiplierWindow;
      let newStreak = 1;
      if (isWithinWindow) {
        newStreak = Math.min(burnStreak + 1, maxMultiplier);
        setBurnStreak(newStreak);
        if (newStreak > 1) setShowMultiplier(true);
      } else {
        setBurnStreak(1);
        setShowMultiplier(false);
      }
      setLastBurnTime(now);
      const currentMultiplier = newStreak;
      if (multiplierTimeoutRef.current) clearTimeout(multiplierTimeoutRef.current);
      multiplierTimeoutRef.current = setTimeout(() => {
        setShowMultiplier(false);
        setBurnStreak(0);
        setLastBurnTime(0);
      }, multiplierWindow);
      setBeraEyesFire(true);
      if (burnAmount > 1000000) {
        setScreenShake(true);
        setTimeout(() => setScreenShake(false), 500);
      }
      setBurnIntensity((prev) => {
        const baseIntensity = burnAmount / 1000000;
        const multipliedIntensity = baseIntensity * (1 + (currentMultiplier - 1) * 0.5);
        return Math.min(prev + multipliedIntensity, 10);
      });
      if (address && burn.from?.toLowerCase() === address.toLowerCase())
        setUserBurnCount((prev) => prev + 1);
      const eyeFireDuration = 2500 + (currentMultiplier - 1) * 300;
      setTimeout(() => setBeraEyesFire(false), eyeFireDuration);
    },
    [address, burnStreak, lastBurnTime, multiplierWindow, maxMultiplier],
  );

  return {
    beraEyesFire,
    burnIntensity,
    screenShake,
    userBurnCount,
    burnStreak,
    showMultiplier,
    triggerBurnAnimation,
    resetAnimations,
    MULTIPLIER_WINDOW: multiplierWindow,
    MAX_MULTIPLIER: maxMultiplier,
  };
}
